/*
PROV: COHINT037.BF.CHECKBOXGROUP
REQ: AUD-UX-10, AUD-UX-11, UX-BASE-05, SYS-ARCH-15
WHY: Accessible multi-select primitive for small closed enums (3-8 options).
     Wraps a vertical list of Radix Checkbox rows in a native fieldset/legend
     group, encapsulating the fieldset/legend/aria-describedby pattern so
     callers cannot compose it incorrectly. No success state — silent success
     principle applies. Used by the classifications admin page for
     allowed_flow_types, and reusable for any future closed-set multi-pick
     (roles, scopes, permission lists).
*/

import React, { useCallback, useId, useMemo, useState } from "react";
import { Checkbox } from "./controls";
import { HelperText, ErrorText } from "./controls";
import { cn } from "./lib/utils";

export type CheckboxGroupOption = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
};

export type CheckboxGroupProps = {
  options: CheckboxGroupOption[];

  /** Controlled value. When provided, `onChange` should also be provided. */
  value?: string[];
  /** Uncontrolled initial value. Ignored when `value` is provided. */
  defaultValue?: string[];
  onChange?: (next: string[]) => void;

  /** Serialises selections as repeated hidden inputs for native form submit. */
  name?: string;

  /** Disables the entire group. Individual items may also opt out via option.disabled. */
  disabled?: boolean;
  /** Sets aria-invalid on every item and styles the ErrorText slot. */
  invalid?: boolean;

  /** Optional group label rendered as the <legend>. */
  label?: React.ReactNode;
  /** Optional helper text below the group. */
  helpText?: React.ReactNode;
  /** Optional error text below the group. Pairs with `invalid`. */
  errorText?: React.ReactNode;

  className?: string;
};

function CheckboxGroup({
  options,
  value,
  defaultValue,
  onChange,
  name,
  disabled = false,
  invalid = false,
  label,
  helpText,
  errorText,
  className,
}: CheckboxGroupProps) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<string[]>(defaultValue ?? []);
  const selected = isControlled ? value! : internal;

  const groupId = useId();
  const labelId = `${groupId}-label`;
  const helpId = `${groupId}-help`;
  const errorId = `${groupId}-error`;

  const describedBy = useMemo(() => {
    const parts: string[] = [];
    if (helpText) parts.push(helpId);
    if (errorText) parts.push(errorId);
    return parts.length ? parts.join(" ") : undefined;
  }, [helpText, errorText, helpId, errorId]);

  const handleToggle = useCallback(
    (optionValue: string, checked: boolean) => {
      const next = checked
        ? Array.from(new Set([...selected, optionValue]))
        : selected.filter((v) => v !== optionValue);
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [selected, isControlled, onChange],
  );

  return (
    <fieldset
      disabled={disabled}
      aria-labelledby={label ? labelId : undefined}
      aria-describedby={describedBy}
      aria-invalid={invalid ? "true" : undefined}
      className={cn("m-0 p-0 border-0 min-w-0", className)}
    >
      {label !== undefined && label !== null && (
        <legend
          id={labelId}
          className="m-0 mb-2 p-0 text-sm leading-none font-medium text-brand-900 dark:text-brand-100"
        >
          {label}
        </legend>
      )}
      <div className="flex flex-col gap-2">
        {options.map((option) => {
          const itemId = `${groupId}-item-${option.value}`;
          const checked = selected.includes(option.value);
          const itemDisabled = disabled || !!option.disabled;
          return (
            <div key={option.value} className="flex items-center gap-2">
              <Checkbox
                id={itemId}
                checked={checked}
                disabled={itemDisabled}
                aria-invalid={invalid ? "true" : undefined}
                onCheckedChange={(next) => handleToggle(option.value, next === true)}
              />
              <label
                htmlFor={itemId}
                className={cn(
                  "text-sm text-brand-900 dark:text-brand-100 select-none",
                  itemDisabled && "opacity-50 cursor-not-allowed",
                  !itemDisabled && "cursor-pointer",
                )}
              >
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      {name &&
        selected.map((v) => (
          <input key={v} type="hidden" name={name} value={v} />
        ))}
      {helpText && (
        <HelperText id={helpId} className="mt-1.5">
          {helpText}
        </HelperText>
      )}
      {errorText && (
        <ErrorText id={errorId} className="mt-1.5">
          {errorText}
        </ErrorText>
      )}
    </fieldset>
  );
}

export { CheckboxGroup };
